import { ApiError, useUser } from '@berky/auth'
import {
  Button,
  Combobox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  toast,
} from '@berky/ui'
import { useDeferredValue, useState } from 'react'

import { useFeedback, useFeedbackAssignees, useFeedbackAssignment } from '../api'
import type { FeedbackAssignee, FeedbackEntry } from '../types'

export function FeedbackAssignmentSummary({
  entry,
}: {
  entry: Pick<FeedbackEntry, 'assigneeName'>
}) {
  return (
    <dl className="text-sm" aria-label="Zuständigkeit">
      <div>
        <dt className="text-xs text-muted-foreground">Zuständig</dt>
        <dd>{entry.assigneeName ?? '-'}</dd>
      </div>
    </dl>
  )
}

export function FeedbackAssignmentDialog({
  entry,
  onClose,
}: {
  entry: FeedbackEntry | null
  onClose: () => void
}) {
  return (
    <Dialog
      open={entry !== null}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      {entry && <AssignmentEditor key={entry.id} entry={entry} onClose={onClose} />}
    </Dialog>
  )
}

function AssignmentEditor({
  entry,
  onClose,
}: {
  entry: FeedbackEntry
  onClose: () => void
}) {
  const currentUser = useUser()
  const [assignee, setAssignee] = useState({
    id: entry.assigneeId,
    name: entry.assigneeName,
  })
  const [version, setVersion] = useState(entry.assignmentVersion)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [searchRevision, setSearchRevision] = useState(0)
  const [reloadError, setReloadError] = useState(false)
  const deferredSearch = useDeferredValue(search)
  const people = useFeedbackAssignees(deferredSearch, page)
  const mutation = useFeedbackAssignment(entry.id)
  const detail = useFeedback(entry.id)
  const conflict = mutation.error instanceof ApiError && mutation.error.status === 409
  const options = people.data?.items ?? []

  function resetSearch() {
    setSearch('')
    setPage(1)
    setSearchRevision((revision) => revision + 1)
  }

  async function reload() {
    setReloadError(false)
    const fresh = await detail.refetch()
    if (fresh.isError || !fresh.data) {
      setReloadError(true)
      return
    }
    setAssignee({ id: fresh.data.assigneeId, name: fresh.data.assigneeName })
    setVersion(fresh.data.assignmentVersion)
    resetSearch()
    mutation.reset()
  }

  // Closing the menu briefly restores focus outside this non-modal dialog.
  return (
    <DialogContent
      className="max-h-[90svh] overflow-y-auto"
      onFocusOutside={(event) => event.preventDefault()}
    >
      <DialogHeader>
        <DialogTitle>Zuständigkeit</DialogTitle>
        <DialogDescription>
          Legen Sie eine zuständige Person fest, übernehmen Sie das Feedback selbst oder
          heben Sie die Zuständigkeit auf.
        </DialogDescription>
      </DialogHeader>
      <form
        onSubmit={(event) => {
          event.preventDefault()
          mutation.mutate(
            {
              assigneeId: assignee.id,
              assignmentVersion: version,
            },
            {
              onSuccess: () => {
                toast.success('Zuständigkeit gespeichert')
                onClose()
              },
            },
          )
        }}
        className="space-y-4"
      >
        <fieldset disabled={mutation.isPending || conflict} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="assignee-search">Zuständige Person suchen</Label>
            <Combobox<FeedbackAssignee>
              key={searchRevision}
              inputId="assignee-search"
              maxLength={200}
              items={options}
              value={options.find((person) => person.id === assignee.id) ?? null}
              onChange={(person) => setAssignee(person)}
              onSearch={(value) => {
                setSearch(value)
                setPage(1)
              }}
              getKey={(person) => person.id}
              getLabel={(person) => person.name}
              placeholder="Name suchen"
              emptyText="Keine auswählbare Person gefunden."
              loading={people.isFetching}
            />
            <p className="text-sm text-muted-foreground">
              Aktuell zuständig: {assignee.name ?? '-'}
            </p>
            {people.isPending && (
              <p className="text-sm text-muted-foreground" role="status">
                Personen werden geladen …
              </p>
            )}
            {people.isError && (
              <div role="alert" className="text-sm text-destructive">
                Die Personenauswahl konnte nicht geladen werden.{' '}
                <Button
                  type="button"
                  variant="link"
                  onClick={() => void people.refetch()}
                >
                  Erneut versuchen
                </Button>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={assignee.id === currentUser.id}
                onClick={() => {
                  setAssignee({ id: currentUser.id, name: currentUser.name })
                  resetSearch()
                }}
              >
                Übernehmen
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!assignee.id}
                onClick={() => {
                  setAssignee({ id: null, name: null })
                  resetSearch()
                }}
              >
                Freigeben
              </Button>
            </div>
            {(page > 1 || (people.data?.total ?? 0) > 20) && (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={page <= 1 || people.isFetching}
                  onClick={() => setPage(page - 1)}
                >
                  Vorherige Personen
                </Button>
                <span className="text-xs text-muted-foreground">Seite {page}</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  disabled={people.isFetching || page * 20 >= (people.data?.total ?? 0)}
                  onClick={() => setPage(page + 1)}
                >
                  Weitere Personen
                </Button>
              </div>
            )}
          </div>
        </fieldset>
        {mutation.isError && (
          <div role="alert" className="space-y-2 text-sm text-destructive">
            <p>
              {mutation.error instanceof ApiError
                ? mutation.error.userMessage()
                : 'Die Zuständigkeit konnte nicht gespeichert werden.'}
            </p>
            {conflict && (
              <>
                <p>
                  Laden Sie den aktuellen Stand. Ihre Eingaben in diesem Dialog werden
                  dabei ersetzt.
                </p>
                <Button
                  type="button"
                  variant="outline"
                  disabled={detail.isFetching}
                  onClick={() => void reload()}
                >
                  Aktuellen Stand laden
                </Button>
              </>
            )}
          </div>
        )}
        {reloadError && (
          <p role="alert" className="text-sm text-destructive">
            Der aktuelle Stand konnte nicht geladen werden. Bitte versuchen Sie es erneut.
          </p>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            disabled={mutation.isPending}
            onClick={onClose}
          >
            Abbrechen
          </Button>
          <Button type="submit" disabled={mutation.isPending || conflict}>
            {mutation.isPending ? 'Speichert …' : 'Zuständigkeit speichern'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}
